import type { TReviewLineItem } from "./types";

// Define fields for the edit modal
export type TLineItemFieldDef = {
    key: keyof TReviewLineItem;
    label: string;
    type: "text" | "number" | "dropdown";
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
    /**
     * Editable on split rows only. A split row's amount is the one value the
     * reviewer has to change — it starts at 0 and the row stays invalid until the
     * split adds up to the original — while amounts on ordinary rows come from the
     * source file and remain read-only.
     */
    editableOnSplitRows?: boolean;
    dropdownSource?: "propcode" | "asset" | "investment" | "coaCode" | "coaName";
};

/**
 * Fields that are mandatory on TB/GL/OTB lines but optional on FinJournal lines.
 * FinJournals are posted as-is, so a line without a mapped Entity or Acc No is
 * still saveable and still valid — mirrors the FinJournal exemption in
 * `_missing_mapping_errors` (`core/services/file_processing/processing_service.py`).
 */
export const FINJOURNAL_OPTIONAL_FIELD_KEYS: ReadonlyArray<keyof TReviewLineItem> = [
    "propcode",
    "breAccountNumber",
];

/**
 * File types whose lines carry an investment, so a mapped investment is mandatory.
 * Mirrors `_IDENTIFIER_FILE_TYPES` in
 * `modules/rev_apac/ingestion/file_processing/processing_service.py`. Mapped Asset
 * is never mandatory: entity-level lines name no asset.
 */
export const INVESTMENT_REQUIRED_FILE_TYPES: ReadonlyArray<string> = ["trial_balance", "tb_gl"];

export const lineItemFields: TLineItemFieldDef[] = [
    { key: "trannum", label: "Trannum", type: "text", alwaysReadOnly: true },
    {
        key: "propcode",
        label: "Mapped Entity",
        type: "dropdown",
        dropdownSource: "propcode",
        required: true,
    },
    { key: "localPropcode", label: "Entity", type: "text", alwaysReadOnly: true },
    { key: "asset", label: "Asset", type: "text", alwaysReadOnly: true },
    { key: "mappedAsset", label: "Mapped Asset", type: "dropdown", dropdownSource: "asset" },
    { key: "investment", label: "Investment", type: "text", alwaysReadOnly: true },
    {
        key: "mappedInvestment",
        label: "Mapped Investment",
        type: "dropdown",
        dropdownSource: "investment",
    },
    { key: "sourceSystem", label: "Source System", type: "text", alwaysReadOnly: true },
    { key: "sourceAccountNumber", label: "Source Acc No", type: "text", alwaysReadOnly: true },
    { key: "sourceAccountName", label: "Source Acc Name", type: "text", alwaysReadOnly: true },
    {
        key: "breAccountNumber",
        label: "Acc No",
        type: "dropdown",
        dropdownSource: "coaCode",
        required: true,
    },
    { key: "breAccountName", label: "Acc Name", type: "dropdown", dropdownSource: "coaName" },
    { key: "amount", label: "Amount", type: "text", editableOnSplitRows: true },
    { key: "debit", label: "Debit", type: "text", alwaysReadOnly: true },
    { key: "credit", label: "Credit", type: "text", alwaysReadOnly: true },
    { key: "transactionDate", label: "Date", type: "text", alwaysReadOnly: true },
    { key: "postMonth", label: "Post Month", type: "text", alwaysReadOnly: true },
    { key: "bookNumber", label: "Book Number", type: "number", alwaysReadOnly: true },
    { key: "description", label: "Detail Notes", type: "text" },
    { key: "ref", label: "Ref", type: "text", alwaysReadOnly: true },
    { key: "exchangeRate", label: "Exchange Rate", type: "text" },
    { key: "exchangeRateDate", label: "Exchange Rate Date", type: "text" },
    { key: "displayType", label: "Display Type", type: "text" },
    { key: "localCurrency", label: "Local Currency", type: "text" },
    { key: "localAmount", label: "Local Amount", type: "text" },
];

/**
 * Edit-modal field definitions for a file of `fileType`.
 *
 * Identical to `lineItemFields` except that
 * - on FinJournals, Mapped Entity and Acc No carry no required flag — so the modal
 *   shows no asterisk and the save is not blocked when they are blank;
 * - Mapped Investment is required on the file types that carry an investment.
 */
export function resolveLineItemFields(fileType: string | undefined): TLineItemFieldDef[] {
    const isFinJournal = fileType === "financial_journals";
    const investmentRequired =
        fileType !== undefined && INVESTMENT_REQUIRED_FILE_TYPES.includes(fileType);
    return lineItemFields.map((field) => {
        if (isFinJournal && FINJOURNAL_OPTIONAL_FIELD_KEYS.includes(field.key)) {
            return { ...field, required: false };
        }
        if (field.key === "mappedInvestment" && investmentRequired) {
            return { ...field, required: true };
        }
        return field;
    });
}
